/* eslint-disable max-lines */
import {
  AnsweredQuestionnaire,
  GetSuggestedTreatmentPlanResponse,
  IneligibilityReason,
  PartnerEligibilityControllerService,
  PartnerEligibilityResponse,
  SubscriptionDetailsResponse
} from "@9amhealth/openapi";
import React, { JSX } from "react";
import FunnelKey from "src/constants/funnelKey";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import {
  FUNNEL_QUESTIONNAIRE_ELIGIBILITY,
  FUNNEL_QUESTIONNAIRE_MEDICAL
} from "src/constants/misc";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import type {
  CustomQuestionnaireAnswer,
  CustomQuestionnaireFilterAnswerOptions
} from "src/state/QuestionnaireCubit/QuestionnaireState";
import ShipmentAddressCubit from "src/state/ShipmentAddressCubit/ShipmentAddressCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import {
  authenticationState,
  subscriptionState,
  userPreferences,
  userState
} from "src/state/state";
import { TranslationKey } from "src/types/translationKey";
import EstablishCareStep from "src/ui/components/SignupCustomContent/EstablishCareStep";
import HealthInformationExchangeStep from "src/ui/components/SignupCustomContent/HealthInformationExchangeStep";
import type {
  SignupCustomCampaign,
  SignupCustomCampaignName,
  SignupCustomStep
} from "src/ui/components/SignupCustomContent/SignupCustom.types";
import { SignupCustomStepType } from "src/ui/components/SignupCustomContent/SignupCustom.types";
import SignupCustomIneligibility, {
  IneligibleIcon
} from "src/ui/components/SignupCustomContent/SignupCustomIneligibility";
import GenericCheckoutCustomizedCheckoutScreen from "src/ui/components/SignupCustomContent/campaigns/generic/GenericCheckoutCustomizedCheckoutScreen";
import genericEnrollCheckoutSuccessScreenProps from "src/ui/components/SignupCustomContent/campaigns/generic/GenericEnrollCheckoutSuccessScreenProps";
import assessmentCompleteSuccessPageProps from "src/ui/components/SignupCustomContent/campaigns/generic/GenericFinalSuccessPageProps";
import { SignupCustomStepCheckoutRawContent } from "src/ui/components/SignupCustomContent/steps/SignupCustomStepCheckoutCustomRender";
import SignupCustomStepCheckoutDynamicContent from "../../SignupCustomStepCheckoutDynamicContent/SignupCustomStepCheckoutDynamicContent";
import Translate from "../../Translate/Translate";
import type { SignupSuccessPageProps } from "../SignupSuccessPage";
import HtmlParser from "src/lib/HtmlParser";
import {
  formatPhoneNumberNational,
  formatPhoneNumberUS
} from "src/lib/formatPhoneNumber";
import { findMatchingCampaignByAnswers } from "./findMatchingCampaignByAnswers";

export enum GenericTemplateFunnelLocalStorageKeys {
  ConfirmedSuccessScreen = "9am_<funnel-name>-enrollment-success-seen",
  SuggestionResponseEligible = "9am_<funnel-name>-suggestion-response-eligible-seen",
  CustomStepActionPrefix = "9am_<funnel-name>-custom-step-action-",
  evaluateFunnelType = "9am_<funnel-name>-evaluate-funnel-type",
  healthInformationExchange = "9am_<funnel-name>-health-information-exchange"
}

export function lsKey(funnelKey: FunnelKey, key: string) {
  return key.replace(
    "<funnel-name>",
    `${userState.state.userData?.id}-${funnelKey}`
  );
}

interface FactorySignupCustomCampaignOptions {
  funnelKey: FunnelKey;
  prettyName: string;
  /** Changes text on the initial-ineligible screen
   * @default employer
   **/
  contactForIneligibilityFileCheck?: "9am" | "employer";
  fileCheckIneligibilityAdditionalInfo?: (
    instance: FactorySignupCustomCampaign
  ) => JSX.Element;
  name: SignupCustomCampaignName;
  logo?: string;
  customLogo?: string;
  logoSize?: SignupCustomCampaign["logoSize"];
  answerOverrides?: Record<string, CustomQuestionnaireAnswer[]>;
  filterAnswerOptions?: Record<
    string,
    CustomQuestionnaireFilterAnswerOptions[]
  >;
  stepOrder?: SignupCustomCampaignAvailableSteps[];
  getSubscriptionFromSuggestedTreatmentPlan?: boolean;
  personalisedFinalSuccessScreen?: boolean;
  additionalLegalTextKey?: TranslationKey;
  availabilityChecks?: SignupCustomCampaign["availabilityChecks"];
  signupContactNumber?: string;
}

export enum SignupCustomCampaignAvailableSteps {
  updateEligibilityInformation = "eligibility-updates",
  updateMedicalInformation = "medical-updates",
  eligibility = "eligibility",
  healthInformationExchange = "health-information-exchange",
  register = "register",
  emailVerification = "email-verification",
  shipping = "shipping",
  checkout = "checkout",
  medicalDetails = "medical-details",
  establishCare = "establish-care",
  finalSuccess = "success"
}

export type SubscriptionCoverageType = "cash-pay" | "payer-covered" | "unknown";

export default class FactorySignupCustomCampaign {
  private readonly campaign: SignupCustomCampaign;
  name: SignupCustomCampaignName;
  factoryOptions: FactorySignupCustomCampaignOptions;
  userIsEligible = false;
  answerOverrides: Record<string, CustomQuestionnaireAnswer[] | undefined>;
  filterAnswerOptions: Record<
    string,
    CustomQuestionnaireFilterAnswerOptions[] | undefined
  >;
  stepOrder: SignupCustomCampaignAvailableSteps[] = [
    SignupCustomCampaignAvailableSteps.updateEligibilityInformation,
    SignupCustomCampaignAvailableSteps.eligibility,
    SignupCustomCampaignAvailableSteps.healthInformationExchange,
    SignupCustomCampaignAvailableSteps.register,
    SignupCustomCampaignAvailableSteps.emailVerification,
    SignupCustomCampaignAvailableSteps.shipping,
    SignupCustomCampaignAvailableSteps.updateMedicalInformation,
    SignupCustomCampaignAvailableSteps.medicalDetails,
    SignupCustomCampaignAvailableSteps.establishCare,
    SignupCustomCampaignAvailableSteps.checkout,
    SignupCustomCampaignAvailableSteps.finalSuccess
  ];

  loggingEnabled = false;
  log = (msg: string, ...add: unknown[]) => {
    if (this.loggingEnabled) {
      // eslint-disable-next-line no-console
      console.log(`[FactorySignupCustomCampaign] ${msg}`, ...add);
    }
    logSentryBreadcrumb("factorySignupCustomCampaign", msg);
  };

  constructor(options: FactorySignupCustomCampaignOptions) {
    this.campaign = {
      funnelKey: options.funnelKey,
      prettyName: options.prettyName,
      name: options.name,
      logo: options.logo,
      logoSize: options.logoSize,
      customLogo: options.customLogo,
      availabilityChecks: options.availabilityChecks,
      steps: this.generateStepList
    };

    this.answerOverrides = options.answerOverrides ?? {};
    this.filterAnswerOptions = options.filterAnswerOptions ?? {};
    this.stepOrder = options.stepOrder ?? this.stepOrder;
    this.name = options.name;

    this.factoryOptions = options;

    this.populateDefaultSteps();
  }

  /**
   * Get effective factory options by checking if user's eligibility answers
   * match a more specific campaign's answerOverrides. If so, override
   * display/contact properties from that campaign.
   */
  getEffectiveFactoryOptions(): FactorySignupCustomCampaignOptions {
    const eligibilityAnswers = userState.state.answeredQuestionnaires?.find(
      (q) => q.questionnaireRef.id === FUNNEL_QUESTIONNAIRE_ELIGIBILITY
    );

    if (!eligibilityAnswers?.answers?.json) {
      return this.factoryOptions;
    }

    const userAnswers = eligibilityAnswers.answers
      .json as CustomQuestionnaireAnswer[];
    const matchingCampaign = findMatchingCampaignByAnswers(userAnswers);

    if (!matchingCampaign || matchingCampaign.name === this.name) {
      return this.factoryOptions;
    }

    const matchedOptions = matchingCampaign.factoryOptions;

    return {
      ...this.factoryOptions,
      signupContactNumber:
        matchedOptions.signupContactNumber ??
        this.factoryOptions.signupContactNumber,
      contactForIneligibilityFileCheck:
        matchedOptions.contactForIneligibilityFileCheck ??
        this.factoryOptions.contactForIneligibilityFileCheck,
      fileCheckIneligibilityAdditionalInfo:
        matchedOptions.fileCheckIneligibilityAdditionalInfo ??
        this.factoryOptions.fileCheckIneligibilityAdditionalInfo
    };
  }

  get isTcFunnel() {
    return this.campaign.funnelKey === FunnelKey.transcarent;
  }

  get(): SignupCustomCampaign {
    return this.campaign;
  }

  addedSteps: SignupCustomStep[] = [];
  generateStepList = (): SignupCustomStep[] => {
    this.refreshSteps();
    return this.addedSteps;
  };

  addStep(step: SignupCustomStep): typeof this {
    this.addedSteps.push(step);
    return this;
  }

  addStepsMap: Record<SignupCustomCampaignAvailableSteps, () => void> = {
    [SignupCustomCampaignAvailableSteps.updateEligibilityInformation]: () => {
      const updateInitialEligibilityStep = this.createUpdateQuestionnaireStep({
        questionnaireId: FUNNEL_QUESTIONNAIRE_ELIGIBILITY,
        questionnaireName: "Abbreviated Eligibility",
        name: "Eligibility",
        path: "update-eligibility",
        dataComplete: async () => {
          this.log("updateEligibilityInformation.dataComplete: checking");
          const eligibilityAnswered = await this.checkAnsweredQuestionnaire(
            FUNNEL_QUESTIONNAIRE_ELIGIBILITY
          );

          // If not answered at all, pass (step is complete, user doesn't need to update)
          if (!eligibilityAnswered) {
            this.log(
              "updateEligibilityInformation.dataComplete: not answered, returning true",
              { eligibilityAnswered }
            );
            return true;
          }

          // Check if questionnaire was answered before subscription was cancelled
          const needsRedo =
            await this.checkQuestionnaireAnsweredBeforeCancellation(
              FUNNEL_QUESTIONNAIRE_ELIGIBILITY
            );

          // If it was answered before cancellation, do not pass (force redo)
          if (needsRedo) {
            this.log(
              "updateEligibilityInformation.dataComplete: needs redo, returning false",
              { eligibilityAnswered, needsRedo }
            );
            return false;
          }

          this.log(
            "updateEligibilityInformation.dataComplete: completed, returning true",
            { eligibilityAnswered, needsRedo }
          );
          return true;
        }
      });
      this.addStep(updateInitialEligibilityStep);
    },
    [SignupCustomCampaignAvailableSteps.updateMedicalInformation]: () => {
      const updateInitialEligibilityStep = this.createUpdateQuestionnaireStep({
        questionnaireId: FUNNEL_QUESTIONNAIRE_MEDICAL,
        questionnaireName: "Medical Information",
        name: "Medical Information",
        path: "update-medical",
        dataComplete: async () => {
          this.log("updateMedicalInformation.dataComplete: checking");
          const [eligibilityAnswered, medicalAnswered] = await Promise.all([
            this.checkAnsweredQuestionnaire(FUNNEL_QUESTIONNAIRE_ELIGIBILITY),
            this.checkAnsweredQuestionnaire(FUNNEL_QUESTIONNAIRE_MEDICAL)
          ]);

          // pass if medical or eligibility not answered
          if (
            eligibilityAnswered === undefined ||
            medicalAnswered === undefined
          ) {
            this.log(
              "updateMedicalInformation.dataComplete: not answered, returning true",
              { eligibilityAnswered, medicalAnswered }
            );
            return true;
          }

          const eligibilityAnsweredDate = new Date(
            eligibilityAnswered.timestamp
          );
          const medicalAnsweredDate = new Date(medicalAnswered.timestamp);

          // do not pass if eligibility was answered after medical
          if (eligibilityAnsweredDate > medicalAnsweredDate) {
            this.log(
              "updateMedicalInformation.dataComplete: eligibility answered after medical, returning false",
              { eligibilityAnsweredDate, medicalAnsweredDate }
            );
            return false;
          }

          // Check if questionnaires were answered before subscription was cancelled
          const needsRedoEligibility =
            await this.checkQuestionnaireAnsweredBeforeCancellation(
              FUNNEL_QUESTIONNAIRE_ELIGIBILITY
            );
          const needsRedoMedical =
            await this.checkQuestionnaireAnsweredBeforeCancellation(
              FUNNEL_QUESTIONNAIRE_MEDICAL
            );

          // If either was answered before cancellation, do not pass (force redo)
          if (needsRedoEligibility || needsRedoMedical) {
            this.log(
              "updateMedicalInformation.dataComplete: needs redo, returning false",
              { needsRedoEligibility, needsRedoMedical }
            );
            return false;
          }
          this.log(
            "updateMedicalInformation.dataComplete: completed, returning true",
            { needsRedoEligibility, needsRedoMedical }
          );
          return true;
        }
      });
      this.addStep(updateInitialEligibilityStep);
    },
    [SignupCustomCampaignAvailableSteps.eligibility]: () => {
      const initialEligibilityStep = this.createEligibilityStep({
        eligibilityQuestionnaireId: FUNNEL_QUESTIONNAIRE_ELIGIBILITY,
        questionnaireName: "Abbreviated Eligibility"
      });

      const updateDataStep = this.addedSteps.find(
        (s) => s.path === "update-eligibility"
      );

      if (!updateDataStep) {
        throw new Error(
          "SignupCustomCampaign.addStepsMap.medicalDetailsEligibleCheck: Could not find update data step"
        );
      }

      this.addStep(initialEligibilityStep).addStep(
        this.createIneligibilityStep({
          eligibilityType: "initial",
          path: "initial-ineligible",
          dataCompleteCheck: this.completedInitialEligibilityCheck,
          resolveStep: updateDataStep
        })
      );
    },
    [SignupCustomCampaignAvailableSteps.healthInformationExchange]: () => {
      this.addStep(
        this.createHealthInformationExchangeStep({
          name: translate("healthInfoExchange"),
          path: "health-information-exchange"
        })
      );
    },
    [SignupCustomCampaignAvailableSteps.register]: () => {
      this.addStep(this.createAccountStep());
      this.addStep(this.acceptConsentsStep());
    },
    [SignupCustomCampaignAvailableSteps.emailVerification]: () => {
      this.addStep(this.createEmailVerificationStep());
    },
    [SignupCustomCampaignAvailableSteps.shipping]: () => {
      const shippingStep = this.createShippingInfoStep();
      this.addStep(shippingStep).addStep(
        this.createIneligibilityStep({
          eligibilityType: "shipping",
          path: "data-ineligible",
          dataCompleteCheck: this.checkEligibilityAfterShipping,
          resolveStep: shippingStep
        })
      );
    },
    [SignupCustomCampaignAvailableSteps.medicalDetails]: () => {
      const medicalDetailsStep = this.createMedicalDetailsStep({
        medicalQuestionnaireId: FUNNEL_QUESTIONNAIRE_MEDICAL,
        questionnaireName: "Medical Information",
        path: "medical-details",
        name: "Medical Details"
      });

      const updateDataStep = this.addedSteps.find(
        (s) => s.path === "update-eligibility"
      );

      if (!updateDataStep) {
        throw new Error(
          "SignupCustomCampaign.addStepsMap.medicalDetailsEligibleCheck: Could not find update data step"
        );
      }

      this.addStep(medicalDetailsStep).addStep(
        this.createIneligibilityStep({
          eligibilityType: "medical",
          resolveStep: updateDataStep,
          path: "ineligible-medical-details",
          dataCompleteCheck: this.checkEligibilityAfterMedicalDetails
        })
      );
    },
    [SignupCustomCampaignAvailableSteps.checkout]: () => {
      this.addStep(this.createCheckoutStep());
    },
    [SignupCustomCampaignAvailableSteps.establishCare]: () => {
      this.addStep(
        this.createEstablishCareStep({
          name: "Establish Care",
          path: "establish-care"
        })
      );
    },
    [SignupCustomCampaignAvailableSteps.finalSuccess]: () => {
      this.addStep(this.createFinalSuccessStep());
    }
  };

  subscriptionCoverageType?: SubscriptionCoverageType;

  populateDefaultSteps(): typeof this {
    for (const step of this.stepOrder) {
      try {
        this.addStepsMap[step]();
      } catch (e) {
        reportErrorSentry(e);
      }
    }

    return this;
  }

  refreshSteps = (): void => {
    this.addedSteps = [];
    this.populateDefaultSteps();
  };

  /**
   * Checkers
   */
  checkDraftOrActiveSubscription = (): boolean => {
    const subs = subscriptionState.filterAllSubscriptions({
      status: [
        SubscriptionDetailsResponse.status.DRAFT,
        SubscriptionDetailsResponse.status.ACTIVE
      ],
      metadataPartnerFunnel: [this.campaign.funnelKey]
    }) as SubscriptionDetailsResponse[] | undefined;

    return Boolean(subs?.length);
  };

  checkActiveSubscription = (): boolean => {
    return (
      subscriptionState.filterAllSubscriptions({
        status: [SubscriptionDetailsResponse.status.ACTIVE],
        metadataPartnerFunnel: [this.campaign.funnelKey]
      }).length > 0
    );
  };

  checkAnsweredQuestionnaire = async (
    questionnaireId: string
  ): Promise<AnsweredQuestionnaire | undefined> => {
    return await userState.checkUserHasAnsweredQuestionnaire(
      questionnaireId,
      false
    );
  };

  checkQuestionnaireAnsweredBeforeCancellation = async (
    questionnaireId: string
  ): Promise<boolean> => {
    const activeSubscription = subscriptionState.state.allSubscriptions?.find(
      (s) => s.status === SubscriptionDetailsResponse.status.ACTIVE
    );

    // If user has active subscription, no need to redo
    if (activeSubscription) {
      return false;
    }

    // Check if questionnaire has been answered at all
    const questionnaireAnswered = userState.state.answeredQuestionnaires?.find(
      (q) => q.questionnaireRef.id === questionnaireId
    );
    const questionnaireAnsweredDate = questionnaireAnswered?.timestamp;
    // Find the most recent finished subscription
    const finishedSubscriptions =
      subscriptionState.state.allSubscriptions?.filter(
        (s) => s.status === SubscriptionDetailsResponse.status.FINISHED
      ) ?? [];

    const inactiveSubscriptionDate =
      finishedSubscriptions.length > 0
        ? finishedSubscriptions.reduce((latest, sub) => {
            if (!latest.cancellationDate) {
              return sub;
            }
            if (!sub.cancellationDate) {
              return latest;
            }
            return new Date(sub.cancellationDate) >
              new Date(latest.cancellationDate)
              ? sub
              : latest;
          }, finishedSubscriptions[0]).cancellationDate
        : undefined;

    // Determine if needs redo: questionnaire answered BEFORE cancellation
    const needsRedo =
      questionnaireAnsweredDate &&
      inactiveSubscriptionDate &&
      questionnaireAnsweredDate < inactiveSubscriptionDate;

    // If questionnaire was answered BEFORE cancellation, it's stale and needs redo
    if (needsRedo) {
      return true;
    }

    return false;
  };

  checkUserHasShippingAddress = async (): Promise<boolean> => {
    try {
      const userShippingAddressResponse =
        await ShipmentAddressCubit.loadShipmentAddress();
      const { street, zip, city, state } =
        userShippingAddressResponse.data.addressData;
      const userAddress = street && zip && city && state;
      if (userAddress) {
        return true;
      }
    } catch (e) {
      reportErrorSentry(e);
    }

    return false;
  };

  preSignUpIneligibilityReasons?: PartnerEligibilityResponse["ineligibilityReasons"];
  checkPreSignUpEligibility = async (): Promise<boolean> => {
    try {
      const response =
        await PartnerEligibilityControllerService.checkPreSignUpEligibility();
      const { status } = response.data;
      const reasons = response.data.ineligibilityReasons ?? [];
      this.preSignUpIneligibilityReasons = reasons;
      return status !== PartnerEligibilityResponse.status.INELIGIBLE;
    } catch (e) {
      reportErrorSentry(e);
      return false;
    }
  };

  loadUserSubscriptions = async (): Promise<void> => {
    return subscriptionState.loadAllSubscriptions();
  };

  updateSubscriptionsGetDraftOrActive = async (): Promise<boolean> => {
    await this.loadUserSubscriptions();
    return this.checkDraftOrActiveSubscription();
  };

  updateSubscriptionsGetActive = async (): Promise<boolean> => {
    await this.loadUserSubscriptions();
    return this.checkActiveSubscription();
  };

  updateAnsweredQuestionnaires = async (): Promise<void> => {
    await userState.loadUserAnsweredQuestionnaires();
  };

  completedInitialEligibilityCheck = async (): Promise<boolean> => {
    const [
      preSignUpEligible,
      medicalDetailsEligibility,
      wasMedicalQuestionnaireCompleted
    ] = await Promise.all([
      this.checkPreSignUpEligibility(),
      this.fetchSuggestedTreatmentEligibility(),
      this.checkMedicalQuestionnaireStepCompleted(),
      this.loadUserSubscriptions()
    ]);

    // user has already signed up with active subscription
    const userHasActiveSubscription = this.checkActiveSubscription();
    if (userHasActiveSubscription) {
      return true;
    }

    // User is re-enrolling (has cancelled subscription, no active)
    // Skip the ineligibility step for them - let them proceed to questionnaires
    const { hasFinishedSubscription } = subscriptionState;
    if (hasFinishedSubscription) {
      return true;
    }

    if (wasMedicalQuestionnaireCompleted) {
      return true;
    }
    const userHasDraftOrActiveSubscription =
      this.checkDraftOrActiveSubscription();
    const medicalDetailsNotIneligible =
      medicalDetailsEligibility !==
      GetSuggestedTreatmentPlanResponse.eligibility.INELIGIBLE;

    return (
      userHasDraftOrActiveSubscription &&
      preSignUpEligible &&
      medicalDetailsNotIneligible
    );
  };

  checkEligibilityAfterShipping = async (): Promise<boolean> => {
    const [preSignUpEligible] = await Promise.all([
      this.checkPreSignUpEligibility()
    ]);
    return preSignUpEligible;
  };

  checkEligibilityAfterMedicalDetails = async (): Promise<boolean> => {
    const [preSignUpEligible, medicalDetailsEligibility] = await Promise.all([
      this.checkPreSignUpEligibility(),
      this.fetchSuggestedTreatmentEligibility(),
      this.loadUserSubscriptions()
    ]);

    // user has already signed up
    const userHasActiveSubscription = this.checkActiveSubscription();
    if (userHasActiveSubscription) {
      return true;
    }

    const medicalDetailsEligible =
      medicalDetailsEligibility ===
      GetSuggestedTreatmentPlanResponse.eligibility.ELIGIBLE;
    return preSignUpEligible && medicalDetailsEligible;
  };

  checkMedicalQuestionnaireStepCompleted = async (): Promise<boolean> => {
    await userState.loadUserAnsweredQuestionnaires();
    const answeredMedical = await this.checkAnsweredQuestionnaire(
      FUNNEL_QUESTIONNAIRE_MEDICAL
    );

    // If not answered, step is incomplete
    if (!answeredMedical) {
      return false;
    }

    // Check if questionnaire was answered before subscription was cancelled
    const needsRedo = await this.checkQuestionnaireAnsweredBeforeCancellation(
      FUNNEL_QUESTIONNAIRE_MEDICAL
    );

    // If answered before cancellation, step is incomplete (needs redo)
    if (needsRedo) {
      return false;
    }

    return true;
  };

  lastIneligibilityReasons: IneligibilityReason[] = [];

  fetchSuggestedTreatmentEligibility =
    async (): Promise<GetSuggestedTreatmentPlanResponse.eligibility> => {
      const response =
        await subscriptionState.getEligibilityForCurrentPartner();

      const { eligibility, ineligibilityReasons } = response ?? {};
      this.lastIneligibilityReasons = ineligibilityReasons ?? [];

      if (!eligibility) {
        return GetSuggestedTreatmentPlanResponse.eligibility.INELIGIBLE;
      }

      return eligibility;
    };

  checkLocalStorageKey = (
    funnelKey: FunnelKey,
    localStorageKey: string
  ): boolean | undefined => {
    const checkKey = lsKey(funnelKey, localStorageKey);
    return StorageController.getItem(checkKey) === "true";
  };

  /**
   * Create Steps
   */
  createUpdateQuestionnaireStep(options: {
    questionnaireId: string;
    questionnaireName: string;
    name: string;
    path: string;
    dataComplete: () => Promise<boolean>;
  }): SignupCustomStep {
    return {
      name: options.name,
      path: options.path,
      type: SignupCustomStepType.Questionnaire,
      questionnaireId: options.questionnaireId,
      questionnaireName: options.questionnaireName,
      answerOverrides: this.answerOverrides[options.questionnaireId],
      filterAnswerOptions: this.filterAnswerOptions[options.questionnaireId],
      beforeNextStep: async () => {
        await Promise.all([this.updateAnsweredQuestionnaires()]);
      },
      dataComplete: options.dataComplete
    };
  }

  createEligibilityStep(options: {
    eligibilityQuestionnaireId: string;
    questionnaireName: string;
  }): SignupCustomStep {
    return {
      name: "Eligibility",
      path: "eligibility",
      type: SignupCustomStepType.Questionnaire,
      questionnaireId: options.eligibilityQuestionnaireId,
      questionnaireName: options.questionnaireName,
      answerOverrides: this.answerOverrides[options.eligibilityQuestionnaireId],
      filterAnswerOptions:
        this.filterAnswerOptions[options.eligibilityQuestionnaireId],
      beforeNextStep: async () => {
        await Promise.all([
          this.updateAnsweredQuestionnaires(),
          this.updateSubscriptionsGetDraftOrActive()
        ]);
      },
      dataComplete: async (): Promise<boolean> => {
        this.log("eligibilityStep.dataComplete: checking");
        const [answered] = await Promise.all([
          this.checkAnsweredQuestionnaire(options.eligibilityQuestionnaireId),
          // update user preferencrs in sync
          userPreferences.loadUserPreferences()
        ]);

        // If not answered, step is incomplete
        if (!answered) {
          this.log(
            "eligibilityStep.dataComplete: not answered, returning false",
            { answered }
          );
          return false;
        }

        // Check if questionnaire was answered before subscription was cancelled
        const needsRedo =
          await this.checkQuestionnaireAnsweredBeforeCancellation(
            options.eligibilityQuestionnaireId
          );

        // If answered before cancellation, step is incomplete (needs redo)
        if (needsRedo) {
          this.log(
            "eligibilityStep.dataComplete: needs redo, returning false",
            { answered, needsRedo }
          );
          return false;
        }

        this.log("eligibilityStep.dataComplete: completed, returning true", {
          answered,
          needsRedo
        });
        return true;
      }
    };
  }

  getContactNumber(
    options: {
      useHtmlNonBreakingSpace?: boolean;
    } = {}
  ): {
    displayNumber: string;
    telLink: string;
  } {
    const { useHtmlNonBreakingSpace = false } = options;
    const defaultNumber = "2029329958";
    const effectiveOptions = this.getEffectiveFactoryOptions();
    const campaignNumber = effectiveOptions.signupContactNumber;
    const number = campaignNumber ?? defaultNumber;

    const formattedNumber = formatPhoneNumberNational(number) ?? "";
    const telLink = `tel:${formattedNumber}`;
    const nationalFormat = formatPhoneNumberUS(number) ?? "";

    return {
      displayNumber: useHtmlNonBreakingSpace
        ? nationalFormat.replace(/ /g, "\u00A0")
        : nationalFormat,
      telLink
    };
  }

  createIneligibilityStep(options: {
    path: string;
    eligibilityType?: "medical" | "initial" | "shipping";
    dataCompleteCheck?: () => Promise<boolean>;
    resolveStep?: SignupCustomStep;
  }): SignupCustomStep {
    const preSignupIneligibleReasons = this.preSignUpIneligibilityReasons ?? [];
    const isMedicalIneligibility = options.eligibilityType === "medical";
    const isDataIneligible =
      options.eligibilityType === "initial" ||
      options.eligibilityType === "shipping";
    const ineligibleNotFound = preSignupIneligibleReasons.includes("NOT_FOUND");
    const medicalOrEmploymentTimeIneligible =
      preSignupIneligibleReasons.includes(
        "ELIGIBILITY_NOT_STARTED_DUE_TO_HIRE_DATE"
      );
    const ineligibleFountIneligible =
      preSignupIneligibleReasons.length > 0 && !ineligibleNotFound;

    let title: TranslationKey = "ineligibility.title";
    let description: TranslationKey = "ineligibility.description";
    let showButton = true;
    let icon: React.ComponentProps<typeof SignupCustomIneligibility>["icon"] =
      IneligibleIcon.DOCTOR;

    if (isDataIneligible && ineligibleNotFound) {
      title = "ineligibility.notFound.title";
      description = "ineligibility.notFound.description";
      icon = IneligibleIcon.FORM_EDIT;
    }

    if (isDataIneligible && ineligibleFountIneligible) {
      title = "ineligibility.foundIneligible.title";
      description = "ineligibility.foundIneligible.description";
      icon = IneligibleIcon.FORM_X;
    }

    if (medicalOrEmploymentTimeIneligible) {
      title = "ineligibility.medicalOrEmploymentTimeIneligible.title_amazon";
      description =
        "ineligibility.medicalOrEmploymentTimeIneligible.description_amazon";
      icon = IneligibleIcon.FORM_X;
      showButton = false;
    }

    return {
      name: "Ineligible",
      path: options.path,
      type: SignupCustomStepType.InfoPage,

      render: () => {
        const effectiveOptions = this.getEffectiveFactoryOptions();
        const ineligibilityContact =
          effectiveOptions.contactForIneligibilityFileCheck ?? "employer";
        let showSupportDetails:
          | "initial-questionnaire-ineligible"
          | "contact-after-update"
          | "mistake"
          | null = isMedicalIneligibility ? "mistake" : null;

        const hasSpecialIneligibilityReason =
          ineligibleNotFound ||
          ineligibleFountIneligible ||
          medicalOrEmploymentTimeIneligible;

        const initialIneligible =
          options.eligibilityType === "initial" &&
          !hasSpecialIneligibilityReason &&
          !(ineligibilityContact === "9am");

        if (ineligibilityContact === "9am") {
          showSupportDetails = "contact-after-update";
        }

        if (initialIneligible) {
          showSupportDetails = "initial-questionnaire-ineligible";
        }

        const showIneligibilityReasons =
          this.lastIneligibilityReasons.length > 0;
        const number = this.getContactNumber();

        const getIneligibilityTextFor = (reason: IneligibilityReason) => {
          const contextReason = reason.source?.replace(/_/g, "-") ?? "";
          const contextCampaign = `${contextReason}-${this.campaign.funnelKey}`;
          const withCampaign = translate("ineligibility.reasonText", {
            context: contextCampaign,
            phoneNumber: number.displayNumber,
            telLink: number.telLink
          });
          if (withCampaign) {
            return withCampaign;
          }
          const withReason = translate("ineligibility.reasonText", {
            context: contextReason,
            phoneNumber: number.displayNumber,
            telLink: number.telLink
          });
          return withReason;
        };

        return (
          <SignupCustomIneligibility
            icon={icon}
            hideButton={!showButton}
            buttonText={
              isMedicalIneligibility ? (
                <Translate msg="ineligibility.medical.button" />
              ) : (
                <Translate msg="ineligibility.button" />
              )
            }
            resolveStep={options.resolveStep}
          >
            <nine-spacer s="md"></nine-spacer>
            <h4>
              <Translate
                msg={title}
                variables={{ context: ineligibilityContact }}
              />
            </h4>
            <nine-spacer s="md"></nine-spacer>

            <p
              className="m0 color-c-80 ineligibility-description"
              style={{ maxWidth: "520px", margin: "0 auto" }}
            >
              <Translate
                msg={description}
                variables={{ context: ineligibilityContact }}
              />
            </p>
            <nine-spacer s="md"></nine-spacer>

            {showSupportDetails === "contact-after-update" && (
              <p
                className="m0 color-c-80"
                style={{ maxWidth: "380px", margin: "0 auto" }}
              >
                <Translate
                  msg="help.contactUsAfterEligibilityDataUpdate"
                  variables={{
                    phoneNumber: number.displayNumber,
                    telLink: number.telLink
                  }}
                />{" "}
              </p>
            )}

            {(showSupportDetails === "mistake" ||
              showSupportDetails === "initial-questionnaire-ineligible") && (
              <p
                className="m0 color-c-80"
                style={{ maxWidth: "380px", margin: "0 auto" }}
              >
                <Translate msg="help.contactUs" />{" "}
                <a
                  href={number.telLink}
                  style={{ color: "#000", fontWeight: "500" }}
                >
                  <span className="non-breaking">{number.displayNumber}</span>
                </a>{" "}
                <Translate msg="help.emailSupport" />{" "}
                <a
                  href="mailto:support@join9am.com"
                  style={{ color: "#000", fontWeight: "500" }}
                >
                  support@join9am.com
                </a>
                .
                <br />
                <br />
                <Translate msg="help.requiredEnrollmentHelp" />
              </p>
            )}

            {effectiveOptions.fileCheckIneligibilityAdditionalInfo?.(this)}

            {showIneligibilityReasons && (
              <div className="m0 p0 color-c-80">
                <nine-spacer s="md"></nine-spacer>
                {this.lastIneligibilityReasons.map((reason) => (
                  <div key={`${reason.reason}-${reason.source}`}>
                    {new HtmlParser(getIneligibilityTextFor(reason)).toJsx()}
                    <nine-spacer s="md"></nine-spacer>
                  </div>
                ))}
              </div>
            )}
          </SignupCustomIneligibility>
        );
      },
      dataComplete: async (): Promise<boolean> => {
        this.log("ineligibilityStep.dataComplete: checking", {
          path: options.path,
          eligibilityType: options.eligibilityType
        });
        const result = (await options.dataCompleteCheck?.()) ?? false;
        this.log("ineligibilityStep.dataComplete: result", {
          path: options.path,
          result
        });
        return result;
      }
    };
  }

  createAccountStep(): SignupCustomStep {
    return {
      name: "Register",
      path: "register",
      type: SignupCustomStepType.Authentication,
      dataComplete: async () => {
        this.log("accountStep.dataComplete: checking");
        const fullAuthCheck = [
          userState.state.userData?.email,
          userState.state.userData?.passwordSet,
          authenticationState.state.authenticationStatus === "authenticated"
        ];
        const passwordlessUserCheck = [
          userState.state.userData?.email,
          authenticationState.state.authenticationStatus === "authenticated",
          subscriptionState.hasActiveSubscription
        ];
        const ssoPartner = {
          transcarent: [this.isTcFunnel],
          amazon: [
            this.campaign.funnelKey === FunnelKey.amazon,
            userState.state.userData?.email,
            userState.state.userData?.emailVerified
          ]
        };

        const result =
          ssoPartner.transcarent.every(Boolean) ||
          ssoPartner.amazon.every(Boolean) ||
          fullAuthCheck.every(Boolean) ||
          passwordlessUserCheck.every(Boolean);

        this.log("accountStep.dataComplete: result", {
          result,
          fullAuthCheck,
          passwordlessUserCheck,
          ssoPartner
        });
        return result;
      }
    };
  }

  createEmailVerificationStep(): SignupCustomStep {
    return {
      name: "Email Verification",
      path: "email-verification",
      type: SignupCustomStepType.EmailVerification,
      dataComplete: async () => {
        await userState.loadUserInfo();
        return userState.state.userData?.emailVerified === true;
      }
    };
  }

  acceptConsentsStep(): SignupCustomStep {
    return {
      name: "Consents",
      path: "consents",
      type: SignupCustomStepType.Consents,
      beforeNextStep: async () => {
        return userState.getConsentStatus();
      },
      dataComplete: async () => {
        const result = userState.hasConsentsNotAccepted === false;
        this.log("consentsStep.dataComplete: result", {
          result,
          hasConsentsNotAccepted: userState.hasConsentsNotAccepted
        });
        return result;
      }
    };
  }

  createShippingInfoStep(): SignupCustomStep {
    return {
      name: "Shipping Address",
      path: "shipping",
      type: SignupCustomStepType.ShippingAddress,
      dataComplete: async () => {
        this.log("shippingStep.dataComplete: checking");
        const result = await this.checkUserHasShippingAddress();
        this.log("shippingStep.dataComplete: result", {
          result
        });
        return result;
      },
      shippingFormPageProps: {
        title: translate("yourDetails"),
        subtitle: (
          <span
            style={{
              display: "block",
              maxWidth: `${APP_CONTENT_WIDTH_WITHOUT_PADDING}px`,
              margin: "0 auto"
            }}
          >
            <Translate msg="enterShippingInformation" />
          </span>
        )
      }
    };
  }

  createCheckoutStep(): SignupCustomStep {
    return {
      name: "Review Checkout",
      path: "checkout",
      type: SignupCustomStepType.InfoPage,
      beforeNextStep: async () => {
        await this.updateSubscriptionsGetDraftOrActive();
      },
      dataComplete: async () => {
        this.log("checkoutStep.dataComplete: checking");
        const result = this.checkActiveSubscription();
        this.log("checkoutStep.dataComplete: result", { result });
        return result;
      },
      render: () => {
        return (
          <SignupCustomStepCheckoutDynamicContent
            render={(dynamicProps) => (
              <SignupCustomStepCheckoutRawContent
                cartItems={dynamicProps.items}
                discount={dynamicProps.discount}
                content={{
                  programTitle: dynamicProps.program_title,
                  title: dynamicProps.title,
                  subtitle: dynamicProps.subtitle,
                  showMoneyBackGuarantee: false,
                  infoSections: () => (
                    <GenericCheckoutCustomizedCheckoutScreen
                      checkMetadataPartnerFunnel={this.campaign.funnelKey}
                      totalPrice={dynamicProps.totalPrice}
                      eligibleFoeMedicalBilling={
                        dynamicProps.eligibleForMedicalBilling
                      }
                    />
                  ),
                  legalAppendix: dynamicProps.legalAppendix,
                  cartSection: dynamicProps.cartSection,
                  additionalLegalTextKey:
                    this.factoryOptions.additionalLegalTextKey ??
                    userState.payerAdditionalLegalTextKey
                }}
                showShipping={false}
                totalPrice={dynamicProps.totalPrice}
                totalPriceFirstMonth={dynamicProps.totalPriceFirstMonth}
                labTestsIncluded={false}
                paymentInterval={dynamicProps.paymentInterval}
                subscriptionId={dynamicProps.subscriptionId}
              />
            )}
          />
        );
      }
    };
  }

  createCheckoutSuccessStep(options: {
    dataCompleteCheck?: () => Promise<boolean>;
  }): SignupCustomStep {
    return {
      name: "Success - Next Steps",
      path: "checkout-success",
      type: SignupCustomStepType.SuccessPage,
      successPageProps: genericEnrollCheckoutSuccessScreenProps(),
      beforeNextStep: async () => {
        const key = lsKey(
          this.campaign.funnelKey,
          GenericTemplateFunnelLocalStorageKeys.ConfirmedSuccessScreen
        );

        StorageController.setItem(key, "true");
      },
      dataComplete: async (): Promise<boolean> => {
        this.log("checkoutSuccessStep.dataComplete: checking");
        if (options.dataCompleteCheck) {
          const optionCheck = await options.dataCompleteCheck();
          if (optionCheck) {
            this.log(
              "checkoutSuccessStep.dataComplete: optionCheck passed, returning true"
            );
            return true;
          }
        }

        const funnelKeyDefined = this.checkLocalStorageKey(
          this.campaign.funnelKey,
          GenericTemplateFunnelLocalStorageKeys.ConfirmedSuccessScreen
        );

        if (funnelKeyDefined) {
          this.log(
            "checkoutSuccessStep.dataComplete: funnelKeyDefined, returning true",
            { funnelKeyDefined }
          );
          return true;
        }

        this.log("checkoutSuccessStep.dataComplete: returning false", {
          funnelKeyDefined
        });
        return false;
      },
      action: (navigate) => {
        navigate("/app", { replace: true });
      }
    };
  }

  createMedicalDetailsStep(options: {
    medicalQuestionnaireId: string;
    questionnaireName: string;
    path: string;
    name: string;
  }): SignupCustomStep {
    return {
      name: options.name,
      path: options.path,
      type: SignupCustomStepType.Questionnaire,
      questionnaireId: options.medicalQuestionnaireId,
      questionnaireName: options.questionnaireName,
      beforeNextStep: async () => {
        await Promise.all([
          this.updateAnsweredQuestionnaires(),
          this.loadUserSubscriptions()
        ]);
      },
      dataComplete: async () => {
        this.log("medicalDetailsStep.dataComplete: checking");
        const [completedQuestionnaire] = await Promise.all([
          this.checkMedicalQuestionnaireStepCompleted()
        ]);
        this.log("medicalDetailsStep.dataComplete: result", {
          completedQuestionnaire
        });
        return completedQuestionnaire;
      }
    };
  }

  createEstablishCareStep(options: {
    dataCompleteCheck?: () => Promise<boolean>;
    name: string;
    path: string;
  }): SignupCustomStep {
    return {
      name: options.name,
      path: options.path,
      type: SignupCustomStepType.InfoPage,
      dataComplete: async () => {
        this.log("establishCareStep.dataComplete: checking");
        if (options.dataCompleteCheck) {
          const optionCheck = await options.dataCompleteCheck();
          if (optionCheck) {
            this.log(
              "establishCareStep.dataComplete: optionCheck passed, returning true"
            );
            return true;
          }
        }
        const result = userPreferences.isEstablishCareCompleted;
        this.log("establishCareStep.dataComplete: result", {
          result,
          isEstablishCareCompleted: userPreferences.isEstablishCareCompleted
        });
        return result;
      },
      render: () => {
        return <EstablishCareStep />;
      }
    };
  }

  createHealthInformationExchangeStep(options: {
    dataCompleteCheck?: () => Promise<boolean>;
    name: string;
    path: string;
  }): SignupCustomStep {
    return {
      name: options.name,
      path: options.path,
      type: SignupCustomStepType.InfoPage,
      dataComplete: async () => {
        const result = userPreferences.isHealthInformationExchangeCompleted;
        this.log("healthInformationExchangeStep.dataComplete: result", {
          result
        });
        return result;
      },
      render: () => {
        return <HealthInformationExchangeStep />;
      }
    };
  }

  createFinalSuccessStep(): SignupCustomStep {
    const successPageProps = {
      ...assessmentCompleteSuccessPageProps
    } satisfies SignupSuccessPageProps;

    return {
      name: "Success",
      path: "success",
      type: SignupCustomStepType.SuccessPage,
      successPageProps,
      dataComplete: async () => {
        this.log("finalSuccessStep.dataComplete: always returns false");
        return false;
      },
      action: (navigate) => {
        navigate("/app");
      }
    } satisfies SignupCustomStep;
  }
}
